// The route library: what each ride is, where it goes dark, and when it fits. Filter by when
// you'd ride it; open one for its strip, the latest start on the next Sunday, the pace model,
// and "Plan it for Sunday", which opens that day in the week with the route ready to pick.
import { useEffect, useState } from "preact/hooks";
import { homeOf } from "../data/empty";
import { useRoute } from "preact-iso";
import { useMusette, routeDetail } from "../lib/store";
import { RouteMap } from "../components/RouteMap";
import { Profile } from "../components/Profile";
import { todayIso, dateLabel, addDays, dow, dayLong } from "../lib/dates";
import { hhmm } from "../lib/sun";
import { movingMin, clearDarkMin, latestStart, speedKmh } from "../lib/ride";
import { Head } from "../components/Head";
import { wallsFor, routeOptions, londonNow } from "../lib/day";
import { photo } from "../data/photos";
import { fmtDur } from "./Today";
import type { Route } from "../lib/types";

export function Routes() {
  const s = useMusette();
  const [f, setF] = useState<"all" | "tonight" | "weeknight" | "weekend">("all");
  // What fits the rest of today: after work on a weekday, from now at the weekend, inside every rule.
  const t = todayIso();
  const walls = wallsFor(s, t);
  const nowMin = londonNow().min;
  const dw = dow(t);
  const after = dw >= 1 && dw <= 5 ? Math.max(nowMin, ...walls.filter((w) => w.end <= 20 * 60 && w.end > 12 * 60).map((w) => w.end)) : Math.max(nowMin, 8 * 60);
  const tonight = routeOptions(s, t, Math.ceil(after / 5) * 5, Math.ceil(after / 5) * 5 + 90, walls).filter((o) => o.fits && o.start >= nowMin);
  const fitIds = new Set(tonight.map((o) => o.id));
  const list = s.routes.filter((r) => f === "all" || (f === "tonight" ? fitIds.has(r.id) : f === "weeknight" ? r.weeknight : r.weekend)).sort((a, b) => a.km - b.km);
  return (
    <main class="screen signal">
      <Head right={`${s.routes.length} routes`} />
      <section class="stack" style="margin-top: var(--space-3)">
        <a href="/rides" class="label back">← Rides</a>
        <span class="say">The dark stretch matters more than the length.</span>
        <a class="card row-between add-route" href="/routes/new"><span class="note-title">New ground from Komoot</span><span class="label amber">Explore</span></a>
        <a class="card row-between add-route" href="/routes/add"><span class="note-title">Add a route from Komoot</span><span class="label amber">GPX</span></a>
        <div class="segmented text" role="tablist">
          {(["all", "tonight", "weeknight", "weekend"] as const).map((k) => <button type="button" role="tab" aria-selected={f === k} onClick={() => setF(k)}>{k === "all" ? "All" : k === "tonight" ? (dw >= 1 && dw <= 5 ? "Tonight" : "Today") : k === "weeknight" ? "Weeknight" : "Weekend"}</button>)}
        </div>
        {f === "tonight" && <span class="soft">{tonight.length > 1 ? `Out from ${hhmmOf(Math.min(...tonight.map((o) => o.start)))}. Each route below fits your rules and the light.` : "Only the lit city loop fits what's left of today."}</span>}
        {list.map((r) => (
          <a class="card route-card" href={`/routes/${r.id}`}>
            <div class="row-between"><span class="note-title">{r.n}</span><span class="label">{r.weeknight ? "Weeknight" : "Weekend"}</span></div>
            <Strip r={r} />
            <div class="row-between"><span class="label">{r.km} km · {r.gain.toLocaleString("en-GB")} m · {fmtDur(movingMin(r.km, r.gain)).replace(" h ", ":")}</span><span class="label">{r.u0 != null && r.u1 != null ? `${Math.round((r.u1 - r.u0) * 10) / 10} km unlit${r.lightFrom === "osm" ? " (OSM)" : ""}` : r.lit ? (r.lightFrom === "osm" ? "Lit (OSM)" : "Lit") : "Unsurveyed"}</span></div>
          </a>
        ))}
        <span class="label">Still to plot in Komoot: a lit 90-minute weeknight loop, a lit 60-minute loop, and a Saturday route between 52 and 94 km. Measured from your Komoot files; unlit windows from your survey, 14 September.</span>
      </section>
    </main>
  );
}

/** The route as a line from home and back, the unlit window marked, ticks every 10 or 20 km. */
function Strip({ r, big }: { r: Route; big?: boolean }) {
  const step = r.km > 120 ? 20 : 10;
  const ticks: number[] = [];
  for (let k = step; k < r.km; k += step) ticks.push(k);
  return (
    <div class={`strip-r${big ? " big" : ""}`} role="img" aria-label={`${r.km} km${r.u0 != null && r.u1 != null ? `, unlit from ${r.u0} to ${r.u1} km` : ""}`}>
      <span class="sr-line" />
      {r.u0 != null && r.u1 != null && <span class="sr-dark" style={`left:${(r.u0 / r.km) * 100}%;width:${((r.u1 - r.u0) / r.km) * 100}%`} />}
      {ticks.map((k) => <span class="sr-tick" style={`left:${(k / r.km) * 100}%`}>{big && <span class="label">{k}</span>}</span>)}
    </div>
  );
}

const hhmmOf = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

export function RouteCard() {
  const s = useMusette();
  const { params } = useRoute();
  const r = s.routes.find((x) => x.id === params.id);
  if (!r) return <main class="screen signal"><Head right="Routes" /><div class="stack" style="margin-top:var(--space-6)"><span class="soft">Not in the library.</span><a class="text-btn" href="/routes">All routes</a></div></main>;
  const home = homeOf(s);
  const t = todayIso();
  const sun = addDays(t, (7 - dow(t)) % 7 || 7);
  const ls = latestStart(r, sun, home.lat, home.lon, s.settings.bufferMin);
  const clear = clearDarkMin(r);
  const mpk = Math.round((r.gain / r.km) * 10) / 10;
  const [det, setDet] = useState<{ track: [number, number][]; profile: [number, number][] } | null | undefined>(undefined);
  useEffect(() => { routeDetail(r.id).then(setDet); }, [r.id]);
  return (
    <main class="screen signal">
      <Head right={r.weeknight ? "Weeknight" : "Weekend"} />
      <section class="stack" style="margin-top: var(--space-3)">
        <a href="/routes" class="label back">← Routes</a>
        <div class="card photo-card tall">
          <img src={r.photo ?? photo(r.id) ?? photo("routes")} alt="" decoding="async" />
          <div class="photo-cap"><span class="label" style="color:#fff">{r.loop === false ? (r.startKm != null && r.startKm >= 0.5 ? `Point to point, starts ${r.startKm} km away` : "Point to point") : r.startKm != null && r.startKm >= 0.5 ? `A loop, starts ${r.startKm} km from Meadow Mill` : "From Meadow Mill and back"}</span><span class="say" style="color:#fff">{r.n}</span></div>
        </div>
        <div class="stats">
          <div class="card stat"><span class="label">Distance</span><span class="stat-n">{r.km}<small> km</small></span></div>
          <div class="card stat"><span class="label">Climb</span><span class="stat-n">{r.gain.toLocaleString("en-GB")}<small> m</small></span></div>
          <div class="card stat"><span class="label">Moving</span><span class="stat-n">{fmtDur(movingMin(r.km, r.gain)).replace(" h ", ":")}</span></div>
        </div>
        {det?.track && <div class="card map-card"><RouteMap points={det.track} label={`Map of ${r.n}`} h={220} tiles /></div>}
        {det?.profile && (
          <div class="card">
            <div class="row-between"><span class="label ink">Height</span><span class="label">Steepest 500 m {r.steep}%</span></div>
            <Profile points={det.profile} label={`Height profile of ${r.n}, ${r.gain} m of climbing`} marks={r.u0 != null && r.u1 != null ? [{ from: r.u0, to: r.u1, cls: "pf-dark" }] : []} />
            {r.u0 != null && <span class="label">Shaded: unlit, {r.u0} to {r.u1} km</span>}
          </div>
        )}
        {det === null && <a class="card row-between" href={`/routes/add?for=${r.id}`}><span class="soft">Import its GPX from Komoot for the map and the height.</span><span class="label amber">Import</span></a>}
        <div class="card">
          <div class="row-between"><span class="label ink">Where it goes dark</span><span class="label">{r.u0 != null && r.u1 != null ? `${r.u0} to ${r.u1} km` : r.lit ? "Lit all the way" : "Unsurveyed"}</span></div>
          <Strip r={r} big />
          <span class="soft">{r.u0 != null && r.u1 != null && clear != null ? `${Math.round((r.u1 - r.u0) * 10) / 10} km without street lights, behind you ${fmtDur(clear)} into the ride. Your survey, ${r.surveyed ? new Date(`${r.surveyed}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone: "UTC" }) : "14 September"}.` : r.lit ? "Lit all the way: no limit from the light." : "No survey of the lighting yet, so weekday evenings need it finished by sunset. Mark it by importing its GPX again."}</span>
        </div>
        <div class="card">
          {ls != null && <div class="row-between"><span class="label">Latest start, {dateLabel(sun)}</span><span class="note-title">{hhmm(ls)}</span></div>}
          <div class="row-between"><span class="label">Pace, from your model</span><span class="note-title">{Math.round(speedKmh(r.km, r.gain) * 10) / 10} km/h</span></div>
          <div class="row-between"><span class="label">Climb per km</span><span class="note-title">{mpk} m</span></div>
          <div class="row-between"><span class="label">Steepest 500 m</span><span class="note-title">{r.steep}%</span></div>
          {r.osm && <div class="row-between"><span class="label">Lighting, OpenStreetMap</span><span class="note-title">{r.osm.lit + r.osm.likely}% lit</span></div>}
          {r.osm && <span class="label">Tagged lit {r.osm.lit}% · 30 mph streets {r.osm.likely}% · not known {r.osm.unknown}% · unlit {r.osm.unlit}%{r.lightFrom === "survey" ? " · your survey sets the rules" : ""}</span>}
          {r.note && <span class="soft">{r.note}</span>}
        </div>
        <a class="pill wide" href={`/week?d=${sun}`}>Plan it for {dayLong(sun)}</a>
        {r.komoot && <a class="text-btn" href={r.komoot} target="_blank" rel="noopener">Open in Komoot</a>}
      </section>
    </main>
  );
}
